/**
 * Route table — the Stage A ChatGPT-style grammar (2026-08-28):
 *
 *   /login · /signup                      (open; client-side screens)
 *   /                                     → /products/<last product> else /products
 *   /products                             products home (list, create, delete)
 *   /settings                             account / key / model / layout
 *   /products/:pid                               product home (new-chat empty state)
 *   /products/:pid/chats/:sid                        one conversation (two-pane workspace)
 *   /products/:pid/settings                      product settings (name, model, key, notes)
 *   /products/:pid/codebases                     the product's codebases
 *   /products/:pid/infrastructure                the live sources, the runtime map, the findings
 *   /products/:pid/codebases/:rid                one codebase's page (facts, provenance, changes)
 *   /products/:pid/settings/r/:rid               LEGACY (pre 2026-09-02) → /products/:pid/codebases/:rid
 *   /p/:pid/...                           LEGACY (one-letter grammar, 2026-08-28 to 09-02) → /products/:pid/...
 *   /prod/*                               LEGACY → LegacyRedirects (below)
 *
 * Everything except /login and /signup sits behind `RequireAuth` and inside
 * `AppShell` (the ChatGPT-style sidebar: product switcher + session list —
 * shell/AppShell). The SPA probes GET /api/auth/session; 401-family
 * redirects carry `?next=`. A global handler also catches `auth_required`
 * on any API call mid-session (expired cookie) and lands on /login with the
 * return path.
 */

import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';

import { ApiClientError, setAuthRequiredHandler } from './api/client';
import { useRepo, useProducts } from './api/queries';
import { RequireAuth, loginPathWithNext } from './features/auth/RequireAuth';
import { EmptyState } from './features/home/EmptyState';
import { PaletteOverlay, PaletteWiring } from './features/palette';
import { SessionPage } from './features/session/SessionPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { productPath, productSettingsPath, codebasePath, sessionPath } from './lib/deepLink';
import { RouteError } from './lib/errorBoundary';
import { useRouteScope } from './lib/routeScope';
import { PageContainer, Spinner } from './lib/ui';
import { AppShell } from './shell/AppShell';
import { useLastProduct } from './shell/lastProduct';
import { useUiStore } from './stores/uiStore';

// ---------------------------------------------------------------------------
// Pages loaded when first visited (2026-09-08)
// ---------------------------------------------------------------------------

/**
 * The session path (the shell, the chat, the diagram pane, the product home)
 * is what every visit needs and stays in the main chunk. These pages are
 * visited rarely and load on their first visit; each import names the page's
 * own file rather than its feature barrel, so the barrel's other exports do
 * not ride along. Exported so the route table test can name them.
 */
export const lazyPages = {
  LoginPage: lazy(() => import('./features/auth/LoginPage').then((m) => ({ default: m.LoginPage }))),
  SignupPage: lazy(() =>
    import('./features/auth/SignupPage').then((m) => ({ default: m.SignupPage })),
  ),
  ProductsPage: lazy(() =>
    import('./features/products/ProductsPage').then((m) => ({ default: m.ProductsPage })),
  ),
  CodebasesPage: lazy(() =>
    import('./features/products/CodebasesPage').then((m) => ({ default: m.CodebasesPage })),
  ),
  ProductSettingsPage: lazy(() =>
    import('./features/products/ProductSettingsPage').then((m) => ({
      default: m.ProductSettingsPage,
    })),
  ),
  InfrastructurePage: lazy(() =>
    import('./features/liveinfra/InfrastructurePage').then((m) => ({
      default: m.InfrastructurePage,
    })),
  ),
  RepoSettingsPage: lazy(() =>
    import('./features/repo-settings/RepoSettingsPage').then((m) => ({
      default: m.RepoSettingsPage,
    })),
  ),
};

/** What a page's slot shows while its chunk loads: the same centred spinner RequireAuth's probe uses. */
function PageLoading() {
  return (
    <div className="flex h-full items-center justify-center" data-testid="page-loading">
      <Spinner />
    </div>
  );
}

function LazyPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

// ---------------------------------------------------------------------------
// Scope layouts
// ---------------------------------------------------------------------------

/** `/` lands on the last-opened product's home, else the products list. */
/**
 * The landing. A remembered product wins; otherwise the only product does,
 * because a one-product account should not have to click through a list to
 * reach its own conversation. The list is for choosing, and appears when
 * there is a choice (or nothing yet).
 */
export function HomeRedirect() {
  const lastProduct = useLastProduct((s) => s.productId);
  const products = useProducts();

  if (lastProduct !== null) return <Navigate to={productPath(lastProduct)} replace />;
  // A fresh signup lands here with nothing remembered: the products fetch is
  // the only thing between them and a screen, so it looks like RequireAuth's
  // probe (a centred spinner), never a blank page.
  if (products.isPending) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="home-redirect-pending">
        <Spinner label="Finding your products…" />
      </div>
    );
  }
  const only = products.data?.products.length === 1 ? products.data.products[0] : undefined;
  return <Navigate to={only !== undefined ? productPath(only.id) : '/products'} replace />;
}

/** Mirrors `:pid` into the route-scope store (sidebar + palette links). */
export function ProductScope() {
  const { pid = '' } = useParams<'pid'>();
  const setProductScope = useRouteScope((s) => s.setProductScope);

  useEffect(() => {
    setProductScope(pid.length > 0 ? pid : null);
    return () => setProductScope(null);
  }, [pid, setProductScope]);

  return <Outlet />;
}

/**
 * A deleted/mistyped repo deep link ends HERE — a real not-found state
 * (expert audit A1) instead of a functional-looking empty page whose
 * background fetches all 404 silently.
 */
function RepoNotFound({ pid }: { pid: string }) {
  return (
    <PageContainer testId="repo-not-found">
      <h1 className="mt-0 mb-2 text-[length:var(--text-title-size)] leading-[var(--text-title-line)] font-semibold">
        This code isn&rsquo;t here
      </h1>
      <p className="mt-0 text-secondary">
        The link points at a codebase loremap doesn&rsquo;t have. It may have been removed,
        or it belongs to another account.
      </p>
      <p className="text-secondary">
        {pid.length > 0 ? (
          <Link to={productPath(pid)} className="text-(--accent) underline underline-offset-2">
            ← Back to the product
          </Link>
        ) : (
          <Link to="/products" className="text-(--accent) underline underline-offset-2">
            ← All products
          </Link>
        )}
      </p>
    </PageContainer>
  );
}

/**
 * The codebase page's guards + scope (`/products/:pid/codebases/:rid`).
 * Tracks the active repo in uiStore and the route scope store. Guards the
 * route (expert audit A1/A2): an unknown repo gets a real not-found state,
 * and a URL whose product segment doesn't own the repo redirects to the
 * canonical URL.
 */
export function RepoSettingsScope({ children }: { children: ReactNode }) {
  const { pid = '', rid = '' } = useParams<'pid' | 'rid'>();
  const location = useLocation();
  const repo = useRepo(rid);
  const setActiveProject = useUiStore((s) => s.setActiveProject);
  const setRepoScope = useRouteScope((s) => s.setRepoScope);

  useEffect(() => {
    setActiveProject(rid.length > 0 ? rid : null);
    setRepoScope(rid.length > 0 ? rid : null);
    return () => {
      setActiveProject(null);
      setRepoScope(null);
    };
  }, [rid, setActiveProject, setRepoScope]);

  // A1: stop swallowing the 404s — an unknown/foreign repo is said out loud.
  if (repo.isError && repo.error instanceof ApiClientError && repo.error.status === 404) {
    return <RepoNotFound pid={pid} />;
  }

  // A2: the product segment must be the repo's real owner; a bogus/foreign
  // pid silently rendering the repo's page under the wrong product becomes
  // a redirect to the canonical URL (search preserved).
  if (repo.data !== undefined && repo.data.product_id !== pid) {
    return (
      <Navigate to={`${codebasePath(repo.data.product_id, rid)}${location.search}`} replace />
    );
  }

  return <div className="h-full min-w-0">{children}</div>;
}

// ---------------------------------------------------------------------------
// Legacy grammars (/prod/*, /p/*) → the spelled-out one
// ---------------------------------------------------------------------------

/**
 * The one-letter grammar (`/p/:pid`, `/p/:pid/s/:sid`, 2026-08-28 to
 * 2026-09-02; owner: "I don't like how we are using shortening urls with one
 * letters"): every old address maps onto the spelled-out one, hash and query
 * intact. `s/` becomes `chats/`; the pre-2026-09-02 `settings/r/` becomes
 * `codebases/`.
 */
export function LegacyShortRedirect() {
  const { pid = '', '*': splat = '' } = useParams();
  const location = useLocation();
  const rest = splat.replace(/^s\//, 'chats/').replace(/^settings\/r\//, 'codebases/');
  return (
    <Navigate
      to={`/products/${pid}${rest.length > 0 ? `/${rest}` : ''}${location.search}${location.hash}`}
      replace
    />
  );
}

/**
 * Where a codebase page lived until 2026-09-02: under the product's Settings.
 * Bookmarks and the changes banner's old links keep resolving, hash and
 * query intact (`#changes`, `?since=`).
 */
export function LegacyCodebaseRedirect() {
  const { pid = '', rid = '' } = useParams<'pid' | 'rid'>();
  const location = useLocation();
  return <Navigate to={`${codebasePath(pid, rid)}${location.search}${location.hash}`} replace />;
}

/**
 * Old bookmarks and deep links keep working (mounted at `/prod/:pid/*`);
 * the targets are today's spelled-out addresses (lib/deepLink):
 *
 *   /prod/:pid/guide[?session=sid]   → /products/:pid[/chats/:sid]
 *   /prod/:pid/r/:rid/guide[?session]→ /products/:pid[/chats/:sid]
 *   /prod/:pid/r/:rid/repo           → /products/:pid/codebases/:rid
 *   /prod/:pid/r/:rid/notes          → /products/:pid
 *   /prod/:pid/settings              → /products/:pid/settings
 *   /prod/:pid (+ anything else)     → /products/:pid
 */
export function LegacyRedirects() {
  const { pid = '', '*': splat = '' } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sid = params.get('session');
  const segments = splat.split('/').filter((s) => s.length > 0);

  let to = productPath(pid);
  if (segments[0] === 'guide' || (segments[0] === 'r' && segments[2] === 'guide')) {
    to = sid !== null && sid.length > 0 ? sessionPath(pid, sid) : productPath(pid);
  } else if (segments[0] === 'settings') {
    to = productSettingsPath(pid);
  } else if (segments[0] === 'r' && segments[1] !== undefined && segments[2] === 'repo') {
    to = codebasePath(pid, segments[1]);
  }
  return <Navigate to={to} replace />;
}

// ---------------------------------------------------------------------------
// 401 → /login wiring (api.md §1 `auth_required`)
// ---------------------------------------------------------------------------

/**
 * Registers the client-level 401 handler: any protected API call answering
 * `auth_required` (expired/revoked cookie mid-session) redirects to /login
 * carrying the current URL as the return path. No-op while already on the
 * auth screens.
 */
export function AuthRedirectWiring() {
  const navigate = useNavigate();

  useEffect(() => {
    // The handler reads window.location at fire time, so one registration
    // per mount suffices.
    return setAuthRequiredHandler(() => {
      const { pathname, search } = window.location;
      if (pathname === '/login' || pathname === '/signup') return;
      void navigate(loginPathWithNext(pathname, search), { replace: true });
    });
  }, [navigate]);

  return null;
}

// ---------------------------------------------------------------------------
// Route table
// ---------------------------------------------------------------------------

export function RootLayout() {
  return (
    <div className="h-full">
      <Outlet />
      <AuthRedirectWiring />
      <PaletteWiring />
      <PaletteOverlay />
    </div>
  );
}

// A render error anywhere lands on RouteError (2026-09-08): at the shell for
// a page that throws, at the root for the auth screens and the shell itself.
export const routes = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: '/login',
        element: (
          <LazyPage>
            <lazyPages.LoginPage />
          </LazyPage>
        ),
      },
      {
        path: '/signup',
        element: (
          <LazyPage>
            <lazyPages.SignupPage />
          </LazyPage>
        ),
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            errorElement: <RouteError />,
            children: [
              { path: '/', element: <HomeRedirect /> },
              {
                path: '/products',
                element: (
                  <LazyPage>
                    <lazyPages.ProductsPage />
                  </LazyPage>
                ),
              },
              { path: '/settings', element: <SettingsPage /> },
              {
                path: '/products/:pid',
                element: <ProductScope />,
                children: [
                  { index: true, element: <EmptyState /> },
                  { path: 'chats/:sid', element: <SessionPage /> },
                  {
                    path: 'settings',
                    element: (
                      <LazyPage>
                        <lazyPages.ProductSettingsPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: 'codebases',
                    element: (
                      <LazyPage>
                        <lazyPages.CodebasesPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: 'infrastructure',
                    element: (
                      <LazyPage>
                        <lazyPages.InfrastructurePage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: 'codebases/:rid',
                    element: (
                      <RepoSettingsScope>
                        <LazyPage>
                          <lazyPages.RepoSettingsPage />
                        </LazyPage>
                      </RepoSettingsScope>
                    ),
                  },
                  { path: 'settings/r/:rid', element: <LegacyCodebaseRedirect /> },
                  // Unknown product-scoped paths land on the product home,
                  // never a silent blank outlet (audit 2026-08-26).
                  { path: '*', element: <Navigate to="." replace /> },
                ],
              },
              // The pre-Stage-A grammar: every old URL keeps resolving.
              { path: '/prod/:pid/*', element: <LegacyRedirects /> },
              { path: '/prod/:pid', element: <LegacyRedirects /> },
              { path: '/p/:pid/*', element: <LegacyShortRedirect /> },
              { path: '/p/:pid', element: <LegacyShortRedirect /> },
              { path: '*', element: <Navigate to="/products" replace /> },
            ],
          },
        ],
      },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
